import React, { useEffect, useState } from 'react';
import { ChevronDown, ReceiptText } from 'lucide-react';
import { getWithdrawFeeSchedule, WithdrawFeeSchedule as Schedule } from '../../api/payment/transaction.api';

/**
 * Biểu phí rút tiền cho creator đọc TRƯỚC khi rút (hộp quyết toán thù lao, tab Doanh thu, Ví Ca).
 * Mọi con số lấy từ máy chủ (cùng cấu hình dùng lúc rút thật) nên admin đổi phí là chữ đổi theo.
 * Đọc lỗi thì không hiện gì, không đoán số.
 */

const fmt = (n: number) => Math.max(0, Math.round(n || 0)).toLocaleString('vi-VN');
const pct = (n: number) => `${Number(n || 0).toLocaleString('vi-VN', { maximumFractionDigits: 2 })}%`;

/** Ví dụ thực nhận khi rút đúng mức tối thiểu lần đầu, cùng cách làm tròn với máy chủ khi 1 Ca = 1 đồng. */
export const exampleNet = (s: Schedule) => {
  const amount = s.minFirstCa;
  const percentFee = Math.floor((amount * s.firstPercent) / 100);
  return { amount, fee: s.fixedFeeCa + percentFee, net: Math.max(0, amount - s.fixedFeeCa - percentFee) };
};

export interface WithdrawFeeScheduleProps {
  /** Đang ở lần rút đầu hay không (lấy từ máy chủ), để đánh dấu dòng phí đang áp cho người xem. */
  isFirstWithdraw?: boolean | null;
  /** Thu gọn sẵn, chỉ hiện một dòng tóm tắt; bấm để mở. */
  collapsible?: boolean;
}

const WithdrawFeeScheduleBox: React.FC<WithdrawFeeScheduleProps> = ({ isFirstWithdraw, collapsible }) => {
  const [s, setS] = useState<Schedule | null>(null);
  const [open, setOpen] = useState(!collapsible);

  useEffect(() => {
    let alive = true;
    getWithdrawFeeSchedule()
      .then((r) => alive && setS(r))
      .catch(() => alive && setS(null));
    return () => {
      alive = false;
    };
  }, []);

  if (!s) return null;
  const ex = exampleNet(s);
  const mark = (on: boolean) =>
    on ? <span className="ml-2 rounded-full bg-[#EAF7F0] px-2 py-0.5 text-[11px] font-semibold text-[#11683F]">Đang áp cho bạn</span> : null;

  const summary = `Lần đầu ${pct(s.firstPercent)} cộng ${fmt(s.fixedFeeCa)} Ca, từ lần thứ hai ${pct(s.repeatPercent)} cộng ${fmt(s.fixedFeeCa)} Ca`;

  return (
    <div className="rounded-xl border border-[#DEE0E6] bg-white text-sm dark:border-slate-700 dark:bg-slate-800" data-testid="withdraw-fee-schedule">
      <button
        type="button"
        onClick={() => collapsible && setOpen((v) => !v)}
        className={`flex w-full items-center gap-2 p-3 text-left ${collapsible ? 'cursor-pointer' : 'cursor-default'}`}
        aria-expanded={open}
      >
        <ReceiptText size={18} className="shrink-0 text-[#2271B1]" />
        <span className="min-w-0 flex-1">
          <span className="block font-semibold text-[#121C2A] dark:text-white">Biểu phí rút tiền</span>
          {!open && <span className="block text-xs text-[#627184] dark:text-slate-400">{summary}</span>}
        </span>
        {collapsible && (
          <ChevronDown size={18} className={`shrink-0 text-[#627184] transition-transform ${open ? 'rotate-180' : ''}`} />
        )}
      </button>

      {open && (
        <div className="flex flex-col gap-3 border-t border-[#EEF0F4] p-3 dark:border-slate-700">
          <table className="w-full text-left">
            <tbody className="divide-y divide-[#EEF0F4] dark:divide-slate-700">
              <tr>
                <td className="py-1.5 pr-3 text-[#627184] dark:text-slate-400">Lần rút đầu tiên</td>
                <td className="py-1.5 font-medium text-[#121C2A] dark:text-white">
                  {pct(s.firstPercent)} số Ca rút, cộng {fmt(s.fixedFeeCa)} Ca phí cố định
                  {mark(isFirstWithdraw === true)}
                </td>
              </tr>
              <tr>
                <td className="py-1.5 pr-3 text-[#627184] dark:text-slate-400">Từ lần thứ hai</td>
                <td className="py-1.5 font-medium text-[#121C2A] dark:text-white">
                  {pct(s.repeatPercent)} số Ca rút, cộng {fmt(s.fixedFeeCa)} Ca phí cố định
                  {mark(isFirstWithdraw === false)}
                </td>
              </tr>
              <tr>
                <td className="py-1.5 pr-3 text-[#627184] dark:text-slate-400">Rút tối thiểu</td>
                <td className="py-1.5 font-medium text-[#121C2A] dark:text-white">
                  Lần đầu {fmt(s.minFirstCa)} Ca, các lần sau {fmt(s.minRepeatCa)} Ca
                </td>
              </tr>
              {s.maxCa > 0 && (
                <tr>
                  <td className="py-1.5 pr-3 text-[#627184] dark:text-slate-400">Rút tối đa mỗi lần</td>
                  <td className="py-1.5 font-medium text-[#121C2A] dark:text-white">{fmt(s.maxCa)} Ca</td>
                </tr>
              )}
              <tr>
                <td className="py-1.5 pr-3 text-[#627184] dark:text-slate-400">Quy đổi</td>
                <td className="py-1.5 font-medium text-[#121C2A] dark:text-white">1 Ca quy ra {fmt(s.vndPerCa)} đồng trước phí</td>
              </tr>
            </tbody>
          </table>

          <p className="rounded-lg bg-[#F7F9FC] p-2.5 text-[#233040] dark:bg-slate-700 dark:text-slate-200">
            Ví dụ: rút {fmt(ex.amount)} Ca lần đầu thì phí là {fmt(ex.fee)} Ca, bạn nhận về{' '}
            {fmt(ex.net * s.vndPerCa)} đồng.
          </p>

          <ul className="flex list-disc flex-col gap-1 pl-5 text-[#233040] dark:text-slate-300">
            <li>
              Chỉ Ca thu nhập rút được, tức là Ca bạn nhận từ người khác như donate, hội viên kênh, hợp tác với doanh
              nghiệp. Ca bạn tự nạp và Ca bạn tự donate cho chính mình không rút được.
            </li>
            {s.giftHoldDays > 0 && <li>Ca nhận từ donate phải qua {s.giftHoldDays} ngày mới chuyển sang rút được.</li>}
            {s.firstHoldDays > 0 && (
              <li>
                Lệnh rút đầu tiên được giữ {s.firstHoldDays} ngày để kiểm tra chống gian lận, sau đó Cab mới chuyển khoản.
                Từ lần thứ hai không có thời gian giữ này.
              </li>
            )}
            {s.pitEnabled && (
              <li>
                Mỗi lần rút từ {fmt(s.pitThresholdCa)} Ca trở lên, Cab khấu trừ thuế thu nhập cá nhân {pct(s.pitRatePercent)} trước khi
                chuyển khoản.
              </li>
            )}
            <li>Phí trừ thẳng vào số Ca rút. Ô tính phí trong hộp rút tiền cho biết chính xác số bạn nhận về trước khi gửi.</li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default WithdrawFeeScheduleBox;
